if (!customElements.get('product-info')) {
  customElements.define(
    'product-info',
    class ProductInfo extends HTMLElement {
      constructor() {
        super();
        this.input = this.querySelector('.quantity__input');
        this.currentVariant = this.querySelector('.product-variant-id');
        this.submitButton = this.querySelector('[type="submit"]');
        this.stickyContent = this.dataset.stickyContent;
      }

      cartUpdateUnsubscriber = undefined;
      variantChangeUnsubscriber = undefined;

      connectedCallback() {
        if (this.stickyContent === 'true') {
          this.restoreScrollPosition();
          this.toggleStickyAttributes();
        }
        if (!this.input) return;
        this.quantityForm = this.querySelector('.product-form__quantity');
        if (!this.quantityForm) return;
        this.setQuantityBoundries();
        if (!this.dataset.originalSection) {
          this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, this.fetchQuantityRules.bind(this));
        }
        this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
          const sectionId = this.dataset.originalSection ? this.dataset.originalSection : this.dataset.section;
          if (event.data.sectionId !== sectionId) return;
          this.updateQuantityRules(event.data.sectionId, event.data.html);
          this.setQuantityBoundries();
        });
        this.validateCustomOptions();
      }

      restoreScrollPosition() {
        const scrollKey = `scroll-position-${window.location.pathname}`;
        const savedPosition = sessionStorage.getItem(scrollKey);
        
        if (savedPosition) {
          // Restore position after a short delay to ensure layout is ready
          requestAnimationFrame(() => {
            window.scrollTo(0, parseInt(savedPosition));
          });
        }

        // Save position on scroll (throttled)
        let timeout;
        window.addEventListener('scroll', () => {
          if (timeout) return;
          timeout = setTimeout(() => {
            sessionStorage.setItem(scrollKey, window.scrollY);
            timeout = null;
          }, 100);
        });
      }

      toggleStickyAttributes() {
        let lastScrollY = window.scrollY;
        
        const handleScroll = () => {
          const currentScrollY = window.scrollY;
          // Ignore if no scroll change or negative scroll (iOS bounce)
          if (currentScrollY === lastScrollY || currentScrollY < 0) return;

          const scrollDelta = currentScrollY - lastScrollY;
          const viewportHeight = window.innerHeight;
          const overflow = this.offsetHeight - viewportHeight;

          // Only apply logic if element is taller than viewport
          if (overflow > 0) {
            // Get current top value (default to 0 if unset)
            let currentTop = parseFloat(this.style.top) || 0;

            // Calculate new top based on scroll direction
            // Scrolling DOWN (delta > 0): Subtract delta to move content up (show bottom)
            // Scrolling UP (delta < 0): Subtract negative delta (add) to move content down (show top)
            let newTop = currentTop - scrollDelta;

            // Clamp the value:
            // 1. Max: 0 (Can't go lower than top of viewport)
            // 2. Min: -overflow (Can't go higher than showing the bottom edge)
            newTop = Math.min(0, Math.max(-overflow, newTop));

            this.style.top = `${newTop}px`;
          } else {
            // If element fits in viewport, just stick to top
            this.style.top = '0px';
          }
          
          lastScrollY = currentScrollY;
        };
        
        // Ensure position is sticky
        this.style.position = 'sticky';
        
        window.addEventListener('scroll', handleScroll, { passive: true });
        
        // Initial setup: Wait for layout/scroll restoration, then sync state
        // This prevents the "jump" on first scroll after refresh
        setTimeout(() => {
            lastScrollY = window.scrollY; // Sync lastScrollY to actual position
            
            // If we are restored deep in the page, we might want to start showing the bottom
            // or calculate the correct 'top' offset if possible. 
            // For now, syncing lastScrollY prevents the jump.
            // Optionally: handleScroll(); 
        }, 100);
      }

      disconnectedCallback() {
        if (this.cartUpdateUnsubscriber) {
          this.cartUpdateUnsubscriber();
        }
        if (this.variantChangeUnsubscriber) {
          this.variantChangeUnsubscriber();
        }
      }

      setQuantityBoundries() {
        const data = {
          cartQuantity: this.input.dataset.cartQuantity ? parseInt(this.input.dataset.cartQuantity) : 0,
          min: this.input.dataset.min ? parseInt(this.input.dataset.min) : 1,
          max: this.input.dataset.max ? parseInt(this.input.dataset.max) : null,
          step: this.input.step ? parseInt(this.input.step) : 1,
        };

        let min = data.min;
        const max = data.max === null ? data.max : data.max - data.cartQuantity;
        if (max !== null) min = Math.min(min, max);
        if (data.cartQuantity >= data.min) min = Math.min(min, data.step);
        this.input.min = min;

        if (max) {
          this.input.max = max;
        } else {
          this.input.removeAttribute('max');
        }
        this.input.value = min;
        publish(PUB_SUB_EVENTS.quantityUpdate, undefined);
      }

      fetchQuantityRules() {
        const quantityRulesCart = this.querySelector('.quantity__rules-cart');
        if (!quantityRulesCart) return;
      
        if (!this.currentVariant || !this.currentVariant.value) return;
        
        const loadingSpinner = quantityRulesCart.querySelector('.loading__spinner');
        if (loadingSpinner) {
          loadingSpinner.classList.remove('hidden');
        }
      
        fetch(`${this.dataset.url}?variant=${this.currentVariant.value}&section_id=${this.dataset.section}`)
          .then((response) => {
            return response.text();
          })
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            this.updateQuantityRules(this.dataset.section, html);
            this.setQuantityBoundries();
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            if (loadingSpinner) {
              loadingSpinner.classList.add('hidden');
            }
          });
      }

      updateQuantityRules(sectionId, html) {
        const quantityFormUpdated = html.getElementById(`Quantity-Form-${sectionId}`);
        const selectors = ['.quantity__input', '.quantity__rules', '.quantity__label'];
        for (let selector of selectors) {
          const current = this.quantityForm.querySelector(selector);
          const updated = quantityFormUpdated.querySelector(selector);
          if (!current || !updated) continue;
          if (selector === '.quantity__input') {
            const attributes = ['data-cart-quantity', 'data-min', 'data-max', 'step'];
            for (let attribute of attributes) {
              const valueUpdated = updated.getAttribute(attribute);
              if (valueUpdated !== null) {
                current.setAttribute(attribute, valueUpdated);
              } else {
                current.removeAttribute(attribute);
              }
            }
          } else {
            current.innerHTML = updated.innerHTML;
          }
        }
      }

      validateCustomOptions() {
        const productForms = document.querySelectorAll('[id^="product-form-"]');
        productForms.forEach(form => {
            form.removeAttribute("novalidate");
        });
      }
    }
  );
}
